import { Component, OnInit, Input } from "@angular/core";
import { OkrConvergenceService } from "src/app/shared/services/okr-convergence.service";
import * as Highcharts from "highcharts";
import SolidGauge from "highcharts/modules/solid-gauge";
import HighchartsMore from "highcharts/highcharts-more";
import { FilterService } from "../../services/filter.service";
import { takeUntil } from "rxjs/operators";
import { LoggingService } from "src/app/logging.service";
import { Subject } from "rxjs";
import { hasValue } from "src/app/shared/helpers/filter-utils";
import textConfiguration from "src/assets/static-text-configuration.json";

HighchartsMore(Highcharts);
SolidGauge(Highcharts);

interface Metric {
  metric_name: string;
  target_value?: number;
  latest_value?: number;
  metric_color?: string;
}

interface Project {
  project_name: string;
  project_color?: string;
  metrics?: Metric[];
}

interface Goal {
  goal_id: number;
  goal_name: string;
  goal_color?: string;
  goal_progress?: number;
  projects?: Project[];
  chartOptions?: Highcharts.Options;
}

interface KeyResult {
  key_result_title: string;
  key_result_color?: string;
  key_result_progress?: number;
  goals?: Goal[];
  chartOptions?: Highcharts.Options;
}

interface Objective {
  objective_title: string;
  objective_color?: string;
  objective_progress?: number;
  key_results?: KeyResult[];
  chartOptions?: Highcharts.Options;
}

@Component({
  selector: "app-okr-progress",
  templateUrl: "./okr-progress.component.html",
  styleUrls: ["./okr-progress.component.scss"],
})
export class OkrProgressComponent implements OnInit {
  @Input() projectId?: any;
  @Input() isDialog: boolean = false;
  @Input() selectedTab: any;
  okrs: Objective[] = [];
  readonly PAGE_SIZE = 5;
  goalPages: { [key: string]: number } = {};
  isDataLoaded = false;
  showTooltip = false;
  averageChartOptions?: Highcharts.Options;
  Highcharts: typeof Highcharts = Highcharts;
  filterObj: any;
  filterField: any;
  commonText: any = (textConfiguration as any).common;

  private readonly COLOR_PRIORITY: { [key: string]: number } = {
    red: 1,
    amber: 2,
    green: 3,
    gray: 4,
  };
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly okrService: OkrConvergenceService,
    private readonly filterService: FilterService,
    private readonly loggingService: LoggingService
  ) {}

  ngOnInit(): void {
    let firstTime = true;
    if (this.projectId) {
      this.loadOKRProgress();
    }

    this.filterService.setValue("");

    this.filterService.filterValues
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        if (this.selectedTab?.tab !== 2) return;

        this.filterObj = res;
        let awl = false;

        this.filterObj?.inputs?.forEach((element: any) => {
          if (hasValue(element.value, this.loggingService)) {
            awl = true;
          }
        });

        if (firstTime && !awl) {
          this.loadOKRProgress();
          firstTime = false;
        }

        if (awl) {
          this.loadOKRProgress();
          firstTime = true;
        }
      });

    this.filterService.filterFlag.subscribe((ele: any) => {
      this.filterField = ele.field;
    });
  }

  loadOKRProgress(): void {
    let params: any = {};

    if (this.projectId) {
      params = { project_id: this.projectId };
    } else {
      params = this.buildFilterParams();
    }
    this.okrService.getOKRProgress(params).subscribe({
      next: (res: any) => {
        this.okrs = (res.data || []).map((obj: Objective) => ({
          ...obj,
          chartOptions: this.getProgressChartOptions(
            obj.objective_progress ?? 0
          ),
          key_results: (obj.key_results || []).map((kr: KeyResult) => ({
            ...kr,
            chartOptions: this.getProgressChartOptions(
              kr.key_result_progress ?? 0
            ),
            goals: (kr.goals || []).map((g: Goal) => ({
              ...g,
              chartOptions: this.getProgressChartOptions(g.goal_progress ?? 0),
            })),
          })),
        }));

        this.averageChartOptions = this.getProgressChartOptions(
          this.averageObjectiveProgress
        );

        this.isDataLoaded = true;
      },
      error: (err) => {
        console.error(err);
        this.isDataLoaded = true;
      },
    });
  }

  private buildFilterParams(): { [key: string]: any } {
    const queryParams: any = {};

    if (!this.filterObj?.inputs) return queryParams;

    this.filterObj.inputs.forEach((input: any) => {
      const field = input.field;
      const value = input.value;

      if (hasValue(value, this.loggingService)) {
        switch (field) {
          case "portfolio":
            queryParams.portfolio_id = value.id ?? value;
            break;
          case "subportfolio":
            queryParams.subportfolio_id = value.id ?? value;
            break;
          case "program":
            queryParams.program_id = value.id ?? value;
            break;
          case "project":
            queryParams.project_id = value.id ?? value;
            break;
          case "objective":
            queryParams.objective_id = value.id ?? value;
            break;
          case "keyResult":
            queryParams.keyresult_id = value.id ?? value;
            break;
          case "goal":
            queryParams.goal_id = value.id ?? value;
            break;
          default:
            break;
        }
      }
    });

    return queryParams;
  }

  getBarClass(progress: number): string {
    if (progress >= 80) return "okr-green";
    if (progress >= 50) return "okr-amber";
    return "okr-red";
  }

  get averageObjectiveProgress(): number {
    if (!this.okrs || this.okrs.length === 0) return 0;

    const total = this.okrs.reduce(
      (sum, obj) => sum + (obj.objective_progress ?? 0),
      0
    );
    return total / this.okrs.length;
  }

  getMetricClass(metric: Metric): string {
    return metric.metric_color ? `okr-${metric.metric_color}` : "okr-gray";
  }

  getProjectStatus(project: Project): string {
    return project.project_color ? `okr-${project.project_color}` : "okr-gray";
  }

  getObjectiveKRCount(obj: Objective): number {
    return obj.key_results?.length ?? 0;
  }

  getObjectiveGoalCount(obj: Objective): number {
    return (
      obj.key_results?.reduce((acc, kr) => acc + (kr.goals?.length ?? 0), 0) ??
      0
    );
  }

  getObjectiveProjectCount(obj: Objective): number {
    return (
      obj.key_results?.reduce(
        (acc, kr) =>
          acc +
          (kr.goals?.reduce((gAcc, g) => gAcc + (g.projects?.length ?? 0), 0) ??
            0),
        0
      ) ?? 0
    );
  }

  getObjectiveNamesArray(): string[] {
    return this.okrs.map((obj) => obj.objective_title);
  }

  getObjectiveCount(): number {
    return this.okrs.length;
  }

  getTotalPages(goal: Goal): number {
    const count = goal.projects?.length ?? 0;
    return count > 0 ? Math.ceil(count / this.PAGE_SIZE) : 1;
  }
  getCurrentPage(objIndex: number, krIndex: number, goalId: number): number {
    const key = `${objIndex}-${krIndex}-${goalId}`;
    return this.goalPages[key] ?? 0;
  }

  setPage(
    objIndex: number,
    krIndex: number,
    goalId: number,
    dir: number,
    totalProjects: number
  ): void {
    const key = `${objIndex}-${krIndex}-${goalId}`;
    const pageCount = Math.ceil(totalProjects / this.PAGE_SIZE);
    const current = this.goalPages[key] ?? 0;
    const next = Math.max(0, Math.min(current + dir, pageCount - 1));
    this.goalPages[key] = next;
  }

  formatNumber(value?: number | null): string {
    if (value == null) return "-";

    if (value >= 1_000_000_000) {
      return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    }
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (value >= 1_000) {
      return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }
    return value.toString();
  }

  private getProjectScore(project: Project): [number, number[]] {
    const projectColor = project.project_color ?? "gray";
    const projectPriority = this.COLOR_PRIORITY[projectColor] ?? 4;

    const counts: number[] = [0, 0, 0, 0];
    (project.metrics || []).forEach((m) => {
      const metricColor = m.metric_color ?? "gray";
      const idx = (this.COLOR_PRIORITY[metricColor] ?? 4) - 1;
      counts[idx] += 1;
    });
    return [projectPriority, counts];
  }

  sortProjects(projects: Project[]): Project[] {
    return [...(projects || [])].sort((a, b) => {
      const [aPriority, aCounts] = this.getProjectScore(a);
      const [bPriority, bCounts] = this.getProjectScore(b);

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      for (let i = 0; i < aCounts.length; i++) {
        if (aCounts[i] !== bCounts[i]) {
          return bCounts[i] - aCounts[i];
        }
      }

      return 0;
    });
  }

  getProgressChartOptions(progress: number): Highcharts.Options {
    const value = Math.round(progress);

    return {
      chart: {
        type: "solidgauge",
        backgroundColor: "transparent",
        height: "100%",
      },
      tooltip: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      title: undefined,
      credits: { enabled: false },
      pane: {
        startAngle: -90,
        endAngle: 90,
        center: ["50%", "65%"],
        size: "100%",
        background: {
          //@ts-ignore
          innerRadius: "70%",
          outerRadius: "100%",
          shape: "arc",
          backgroundColor: "#eee",
        },
      },
      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickPositions: [],
        stops: [
          [0.5, "#DF5353"],
          [0.8, "#DDDF0D"],
          [1.0, "#55BF3B"],
        ],
      },
      series: [
        {
          type: "solidgauge",
          name: "Progress",
          data: [value],
          innerRadius: "70%",
          radius: "100%",
          dataLabels: {
            y: -20,
            borderWidth: 0,
            useHTML: true,
            format: `<div style="text-align:center">
                     <span style="font-size:14px; font-weight:600">{y}%</span>
                   </div>`,
          },
        },
      ],
    };
  }

  ngOnDestroy() {
    this.filterService.filterCfgs.next({});
    this.filterService.setFilterValue("");
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
